import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles, CircularProgress, Box } from '@material-ui/core';
import { GetApp, Delete, Cancel } from '@material-ui/icons';

import IconButton from 'components/IconButton';
import { IconTooltip } from 'components';
import { OFFLINE_CHAPTER_STATUS } from 'constants';
import { generateUUID } from 'utilities';

const useStyles = makeStyles((theme) => {
  return {
    statusContainer: {
      padding: '0',

      '& .MuiIconButton-label': {
        display: 'grid',
        placeItems: 'center',

        '& > *': {
          gridColumn: '1',
          gridRow: '1',
        },
      },
    },
    status: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: theme.spacing(6),
      height: theme.spacing(6),
      color: 'rgba(0, 0, 0, 0.38)',
      ...theme.typography.caption,
    },
    loadingIcon: {
      '& > svg': {
        color: `${theme.palette.primary.main} !important`,
      },
    },
    cancelIcon: {
      width: theme.spacing(2.5),
      height: theme.spacing(2.5),
    },
    disabled: {
      cursor: 'default',

      '&.Mui-focusVisible': {
        backgroundColor: 'unset',
      },

      '&:hover': {
        backgroundColor: 'unset',
      },
    },
    hidden: {
      display: 'none',
    },
    offlineStatusButton: {
      color: 'rgba(219, 219, 219, 1)', // very similar to theme.palette.grey[300] but not quite
      '& svg': {
        color: `${theme.palette.greyBlue[700]}`,
      },
      '& .MuiTouchRipple-root': {
        zIndex: '-1',
      },
      '&.Mui-focusVisible': {
        outline: '2px solid black',
      },
    },
  };
});

const ContentStatusButton = ({
  className,
  status,
  onClick,
  progress,
  ariaLabelChapterTitle,
  ...props
}) => {
  const classes = useStyles();
  const id = generateUUID();
  const getInnerContent = () => {
    switch (status) {
      default:
      case OFFLINE_CHAPTER_STATUS.NOT_DOWNLOADED:
        return (
          <IconButton
            aria-labelledby={`content-status-button-label-${id}`}
            className={classes.offlineStatusButton}
            data-status="offline-status-button"
            onClick={onClick}
          >
            <GetApp />
          </IconButton>
        );
      case OFFLINE_CHAPTER_STATUS.QUEUE:
        return (
          <IconButton
            aria-disabled="true"
            aria-labelledby={`content-status-button-label-${id}`}
            className={clsx(
              classes.offlineStatusButton,
              classes.status,
              classes.disabled,
            )}
            data-status="offline-status-button"
            onClick={() => {}}
          >
            Queue
          </IconButton>
        );
      case OFFLINE_CHAPTER_STATUS.DOWNLOADING:
        return (
          <IconButton
            className={clsx(
              classes.offlineStatusButton,
              classes.statusContainer,
            )}
            data-status="offline-status-button"
            onClick={onClick}
          >
            <Box className={classes.status}>
              <CircularProgress
                aria-labelledby={`content-status-button-label-${id}-downloading`}
                value={progress}
                variant={
                  typeof progress === 'number' ? 'determinate' : 'indeterminate'
                }
                className={classes.loadingIcon}
                thickness={4.5}
              />
            </Box>
            <Cancel
              aria-labelledby={`content-status-button-label-${id}-cancel-downloading`}
              className={classes.cancelIcon}
            />
          </IconButton>
        );
      case OFFLINE_CHAPTER_STATUS.DOWNLOADED:
        return (
          <IconButton
            aria-labelledby={`content-status-button-label-${id}`}
            className={classes.offlineStatusButton}
            data-status="offline-status-button"
            onClick={onClick}
          >
            <Delete />
          </IconButton>
        );
    }
  };

  const getLabelContent = () => {
    switch (status) {
      default:
      case OFFLINE_CHAPTER_STATUS.NOT_DOWNLOADED:
        return (
          <>
            Download{' '}
            <span className="noTranslate">{ariaLabelChapterTitle}</span>
          </>
        );
      case OFFLINE_CHAPTER_STATUS.QUEUE:
        return (
          <>
            <span className="noTranslate">{ariaLabelChapterTitle}</span>{' '}
            enqueued
          </>
        );
      case OFFLINE_CHAPTER_STATUS.DOWNLOADING:
        return (
          <>
            <span id={`content-status-button-label-${id}-downloading`}>
              Downloading{' '}
              <span className="noTranslate">{ariaLabelChapterTitle}</span>
            </span>
            <span id={`content-status-button-label-${id}-cancel-downloading`}>
              Cancel downloading{' '}
              <span className="noTranslate">{ariaLabelChapterTitle}</span>
            </span>
          </>
        );
      case OFFLINE_CHAPTER_STATUS.DOWNLOADED:
        return (
          <>
            Delete <span className="noTranslate">{ariaLabelChapterTitle}</span>
          </>
        );
    }
  };

  return (
    <>
      <div className={classes.hidden} id={`content-status-button-label-${id}`}>
        {getLabelContent()}
      </div>
      <IconTooltip
        id={id}
        className={clsx(className)}
        {...props}
        title={
          status === OFFLINE_CHAPTER_STATUS.NOT_DOWNLOADED
            ? 'Download'
            : status === OFFLINE_CHAPTER_STATUS.DOWNLOADED
              ? 'Delete'
              : status === OFFLINE_CHAPTER_STATUS.DOWNLOADING
                ? 'Cancel'
                : ''
        }
      >
        {getInnerContent()}
      </IconTooltip>
    </>
  );
};

ContentStatusButton.propTypes = {
  className: PropTypes.string,
  status: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  progress: PropTypes.number,
};

export default ContentStatusButton;
